import React from 'react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Dismiss20Regular, SpinnerIos20Regular } from '@fluentui/react-icons';
import { useSuppressThirdPartyWidgets } from '../../hooks/useSuppressThirdPartyWidgets';
import { usePulseModal } from './PulseModalContext';
import styles from './PulseModal.module.css';

function formatGeneratedAt(value) {
    if (!value) return null;
    try {
        return format(parseISO(value), 'dd/MM/yyyy');
    } catch (e) {
        try {
            return format(new Date(value), 'dd/MM/yyyy');
        } catch (err) {
            return null;
        }
    }
}

function greetingDisplayName(greeting, user) {
    if (greeting) {
        const stripped = greeting.replace(/^good morning\s*/i, '').replace(/,\s*$/, '').trim();
        if (stripped) {
            return stripped;
        }
    }
    const first = (user?.full_name || '').trim().split(/\s+/)[0];
    return first || 'there';
}

function RichText({ text, html, className }) {
    if (html) {
        return <span className={className} dangerouslySetInnerHTML={{ __html: html }} />;
    }
    if (!text) {
        return null;
    }
    return <span className={className}>{text}</span>;
}

function Pill({ pill }) {
    if (!pill?.label) {
        return null;
    }
    return (
        <span
            className={styles.pill}
            style={{
                backgroundColor: pill.background,
                color: pill.color,
            }}
        >
            {pill.label}
        </span>
    );
}

function OpenInLensButton({ appPath, onNavigate }) {
    if (!appPath) {
        return null;
    }
    return (
        <button
            type="button"
            className={styles.deepLinkBtn}
            onClick={() => onNavigate(appPath)}
            aria-label="Open in Lens"
        >
            &#8250;
        </button>
    );
}

function BulletSection({ title, items, variant = 'plain' }) {
    if (!items?.length) {
        return null;
    }

    const boxClass = variant === 'watch' ? styles.watchForBox : styles.intelligenceBox;

    return (
        <section className={boxClass}>
            <p className={styles.sectionTitle}>{title}</p>
            {items.map((item, idx) => {
                const text = typeof item === 'string' ? item : item?.text;
                const html = typeof item === 'string' ? null : item?.html;
                if (!text && !html) {
                    return null;
                }
                return (
                    <div key={idx} className={variant === 'watch' ? styles.watchForItem : styles.intelligenceItem}>
                        <ul className={styles.bulletList}>
                            <li>
                                {html ? (
                                    <span dangerouslySetInnerHTML={{ __html: html }} />
                                ) : (
                                    text
                                )}
                            </li>
                        </ul>
                    </div>
                );
            })}
        </section>
    );
}

function hasActionContent(item) {
    return Boolean(item?.coaching_text || item?.coaching_text_html);
}

function hasWhatMattersToday(wmt) {
    if (!wmt) {
        return false;
    }
    if (hasActionContent(wmt.first_move)) {
        return true;
    }
    return (wmt.secondary_actions || []).some(hasActionContent);
}

function featureTypeLabel(featureType) {
    const raw = String(featureType || '').trim();
    if (!raw) {
        return '';
    }
    const key = raw.toLowerCase();
    if (key === 'task') return 'Task';
    if (key === 'irl') return 'IRL';
    if (key === 'action') return 'Action';
    if (key === 'raid') return 'RAID';
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
}

function ActionItem({ item, isFirstMove, onNavigate }) {
    if (!item?.coaching_text && !item?.coaching_text_html) {
        return null;
    }

    const handleOpen = () => {
        if (item.app_path) {
            onNavigate(item.app_path);
        }
    };

    return (
        <p className={styles.actionItem}>
            {isFirstMove ? <span className={styles.actionHeading}>First move. </span> : null}
            <RichText text={item.coaching_text} html={item.coaching_text_html} />
            {item.app_path ? (
                <>
                    {' '}
                    <button type="button" className={styles.actionLink} onClick={handleOpen} aria-label="Open in Lens">
                        &gt;
                    </button>
                </>
            ) : null}
        </p>
    );
}

function PriorityRow({ task, onNavigate }) {
    const featureType = featureTypeLabel(task.feature_type);
    return (
        <div className={styles.priorityRow}>
            <div className={styles.priorityRowMain}>
                {task.project_name || task.project_name_html ? (
                    <p className={styles.priorityProject}>
                        <RichText text={task.project_name} html={task.project_name_html} />
                    </p>
                ) : null}
                {task.task_title || task.task_title_html ? (
                    <p className={styles.priorityTask}>
                        {featureType ? <span className={styles.priorityTypePrefix}>{featureType}: </span> : null}
                        <RichText text={task.task_title} html={task.task_title_html} />
                    </p>
                ) : null}
                <div className={styles.pillRow}>
                    <Pill pill={task.status_pill} />
                    <Pill pill={task.time_pill} />
                </div>
            </div>
            <OpenInLensButton appPath={task.app_path} onNavigate={onNavigate} />
        </div>
    );
}

function KeyTasksSidebar({ tasks }) {
    if (!tasks?.length) {
        return null;
    }

    return (
        <section className={styles.keyTasksSection}>
            <p className={styles.sectionTitle}>Key Tasks</p>
            {tasks.map((row, idx) => (
                <div key={idx} className={styles.keyTaskSidebarRow}>
                    {row.project_name || row.project_name_html ? (
                        <p className={styles.keyTaskProjectName}>
                            <RichText text={row.project_name} html={row.project_name_html} />
                        </p>
                    ) : null}
                    <p className={styles.keyTaskSidebarTitle}>
                        <RichText text={row.task_title} html={row.task_title_html} />
                    </p>
                    <div className={styles.keyTaskMetaRow}>
                        <Pill pill={row.project_status_pill} />
                        {row.due_date_label && row.due_date_label !== '—' ? (
                            <p className={styles.keyTaskSidebarDue}>
                                <span className={styles.keyTaskDueLabel}>Due </span>
                                <span className={styles.keyTaskDue} style={{ color: row.due_date_color }}>
                                    {row.due_date_label}
                                </span>
                            </p>
                        ) : null}
                    </div>
                </div>
            ))}
        </section>
    );
}

function EditorialPulseBody({ pulseData, onNavigate }) {
    const watchItem = pulseData.watch_for?.[0];
    const hasSidebar = Boolean(
        watchItem?.text || watchItem?.html || pulseData.key_tasks?.length
    );

    return (
        <div className={`${styles.bodyGrid} ${styles.editorialBodyGrid}`}>
            <div className={styles.mainColumn}>
                {pulseData.executive_summary || pulseData.executive_summary_html || pulseData.coaching || pulseData.coaching_html ? (
                    <div className={styles.narrativeBlock}>
                        {pulseData.executive_summary || pulseData.executive_summary_html ? (
                            <p className={styles.narrative}>
                                <RichText
                                    text={pulseData.executive_summary}
                                    html={pulseData.executive_summary_html}
                                />
                            </p>
                        ) : null}
                        {pulseData.coaching || pulseData.coaching_html ? (
                            <p className={styles.narrative}>
                                <RichText text={pulseData.coaching} html={pulseData.coaching_html} />
                            </p>
                        ) : null}
                    </div>
                ) : null}

                {pulseData.composer_priorities?.length ? (
                    <section className={styles.borderedSection}>
                        <p className={styles.sectionTitle}>Today&apos;s priorities</p>
                        {pulseData.composer_priorities.map((task, idx) => (
                            <PriorityRow key={`${task.rank}-${idx}`} task={task} onNavigate={onNavigate} />
                        ))}
                    </section>
                ) : null}

                {pulseData.team_checkins?.length ? (
                    <section className={styles.borderedSection}>
                        <p className={styles.sectionTitle}>Team Check-ins</p>
                        {pulseData.team_checkins.map((member, memberIdx) => (
                            <div key={`${member.team_member}-${memberIdx}`} className={styles.teamMemberBlock}>
                                <p className={styles.teamMemberName}>{member.team_member}</p>
                                {(member.rows || []).map((row, rowIdx) => (
                                    <div key={rowIdx} className={styles.teamCheckinRow}>
                                        <p className={styles.teamCheckinText}>
                                            {row.project}
                                            {row.project && row.task ? ' | ' : ''}
                                            {row.task}
                                        </p>
                                        <div className={styles.pillRow}>
                                            <Pill pill={row.status_pill} />
                                            <Pill pill={row.time_pill} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </section>
                ) : null}

                {pulseData.closing || pulseData.closing_html ? (
                    <p className={styles.closing}>
                        <RichText text={pulseData.closing} html={pulseData.closing_html} />
                    </p>
                ) : null}
            </div>

            {hasSidebar ? (
                <aside className={styles.editorialSidebar}>
                    {watchItem?.text || watchItem?.html ? (
                        <section className={styles.watchForBox}>
                            <p className={styles.sectionTitle}>Watch for</p>
                            <div className={styles.watchForItem}>
                                <p className={styles.narrative}>
                                    <RichText text={watchItem.text} html={watchItem.html} />
                                </p>
                            </div>
                        </section>
                    ) : null}

                    <KeyTasksSidebar tasks={pulseData.key_tasks} />
                </aside>
            ) : null}
        </div>
    );
}

function CoachingPulseBody({ pulseData, onNavigate }) {
    const wmt = pulseData.what_matters_today || {};
    const showWhatMattersToday = hasWhatMattersToday(wmt);

    return (
        <div className={showWhatMattersToday ? styles.bodyGrid : styles.coachingSingleColumn}>
            <div className={styles.mainColumn}>
                {pulseData.coach_narrative || pulseData.coach_narrative_html ? (
                    <p className={styles.narrative}>
                        <RichText text={pulseData.coach_narrative} html={pulseData.coach_narrative_html} />
                    </p>
                ) : null}

                {pulseData.focus_cards?.length ? (
                    <div className={styles.focusCards}>
                        {pulseData.focus_cards.map((card, idx) => (
                            <div key={`${card.label}-${idx}`} className={styles.focusCard}>
                                <p className={styles.focusCardLabel}>{card.label}</p>
                                <p className={styles.focusCardValue}>
                                    <RichText text={card.value} html={card.value_html} />
                                </p>
                                {card.context || card.context_html ? (
                                    <p className={styles.focusCardContext}>
                                        <RichText text={card.context} html={card.context_html} />
                                    </p>
                                ) : null}
                            </div>
                        ))}
                    </div>
                ) : null}

                <BulletSection title="Intelligence" items={pulseData.pattern_emerging} variant="plain" />
                <BulletSection title="Watch for" items={pulseData.watch_for} variant="watch" />
            </div>

            {showWhatMattersToday ? (
                <aside className={styles.sidebar}>
                    <p className={styles.sectionTitle}>What matters today</p>
                    {wmt.first_move ? <ActionItem item={wmt.first_move} isFirstMove onNavigate={onNavigate} /> : null}
                    {(wmt.secondary_actions || []).map((item, idx) => (
                        <ActionItem key={idx} item={item} onNavigate={onNavigate} />
                    ))}
                </aside>
            ) : null}
        </div>
    );
}

const PulseModal = () => {
    const navigate = useNavigate();
    const user = useSelector(state => state?.user?.user);
    const { open, loading, refreshing, pulseData, message, closeModal, refreshPulse } = usePulseModal();

    useSuppressThirdPartyWidgets(open);

    if (!open) {
        return null;
    }

    const handleBackdropClick = e => {
        if (e.target === e.currentTarget) {
            closeModal();
        }
    };

    const handleNavigate = path => {
        closeModal();
        navigate(path);
    };

    const generatedLabel = formatGeneratedAt(pulseData?.generated_at || pulseData?.send_date_uk);
    const isEditorial = pulseData?.prompt_style === 'editorial_v1';

    return (
        <div className={styles.backdrop} onClick={handleBackdropClick} role="presentation">
            <div className={styles.panel} role="dialog" aria-modal="true" aria-labelledby="lens-pulse-title">
                <button type="button" className={styles.closeBtn} onClick={closeModal} aria-label="Close Lens Pulse">
                    <Dismiss20Regular />
                </button>

                <div className={styles.headerRow}>
                    <h2 id="lens-pulse-title" className={styles.title}>
                        Lens Pulse
                    </h2>
                    <div className={styles.headerMeta}>
                        {generatedLabel ? <p className={styles.generatedAt}>Last generated on {generatedLabel}</p> : null}
                        <button type="button" className={styles.refreshBtn} onClick={refreshPulse} disabled={refreshing || loading}>
                            {refreshing ? (
                                <>
                                    <SpinnerIos20Regular className="rotateLoader me-2" /> Refreshing…
                                </>
                            ) : (
                                'Refresh'
                            )}
                        </button>
                    </div>
                </div>

                {loading && !pulseData ? (
                    <div className={styles.loadingState}>Loading your Pulse…</div>
                ) : pulseData ? (
                    <>
                        {message ? <p className={styles.noticeBanner}>{message}</p> : null}
                        <p className={styles.greeting}>Hello {greetingDisplayName(pulseData.greeting, user)}</p>
                        {isEditorial ? (
                            <EditorialPulseBody pulseData={pulseData} onNavigate={handleNavigate} />
                        ) : (
                            <CoachingPulseBody pulseData={pulseData} onNavigate={handleNavigate} />
                        )}
                    </>
                ) : (
                    <p className={styles.emptyState}>{message || 'No Pulse available yet. Try Refresh to generate your latest briefing.'}</p>
                )}
            </div>
        </div>
    );
};

export default PulseModal;
